import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import {
  useConfirmLeave,
  useGuardedNavigate,
  type GuardedNavigate,
} from "../../context/NavigationGuardContext";
import { useSalon } from "../../context/SalonContext";
import { useAuth } from "../../context/AuthContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { useOptionalMessaging } from "../../context/MessagingContext";
import { useOnboardingDashboardVisibility } from "../../hooks/useOnboardingDashboardVisibility";
import { isSalesTabEnabled } from "../../utils/salesTabFlag";
import { isGiftCardsEnabled } from "../../utils/giftCardsFlag";
import { canOpenPayroll } from "../../utils/payrollAccess";
import { isMessagingEnabled } from "../../utils/messagingFlag";
import type { AvailableLocation } from "../../types/featureAccess";
import {
  buildNavSections,
  findActiveSection,
  type NavAccess,
  type NavSection,
} from "./navSections";

/** Who is signed in, for the avatar menu (desktop) and the mobile sheet. */
export interface NavAccount {
  name: string;
  initials: string;
  /** e.g. "Owner" */
  role?: string;
  salonName?: string;
}

export interface NavModel {
  /** Guarded: unsaved changes (e.g. a Reports view) are resolved first. */
  navigate: GuardedNavigate;
  pathname: string;
  sections: NavSection[];
  /** The section holding the current page (marks its rail button). */
  activeSection: NavSection | undefined;
  account: NavAccount | null;
  /** Multi-location staff only (PR A / #399). */
  showLocationSwitcher: boolean;
  availableLocations: AvailableLocation[];
  selectedSalonId: number | null;
  openLocationPicker: () => void;
  /** Owners only, hidden once there are no onboarding cards left. */
  canAccessOnboarding: boolean;
  showChat: boolean;
  chatUnreadCount: number;
  /** Guarded like `navigate`; signs out once unsaved changes are resolved. */
  handleSignOut: () => void;
}

const NO_LOCATIONS: AvailableLocation[] = [];

const initialsOf = (first = "", last = ""): string =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "U";

/**
 * Navigation model shared by the desktop rail (NavBar) and the mobile nav
 * sheet (MobileNavDrawer): the permission-gated sections, Chat / Onboarding /
 * location-switcher visibility, the signed-in account, and sign out.
 *
 * Every exit it hands out is guarded by NavigationGuardContext: `navigate` is
 * the guarded navigate, and sign-out / the location picker go through
 * `confirmLeave`, so a screen with unsaved changes (e.g. a Reports view) gets
 * its Save / Discard changes dialog first. That covers the rail, its
 * section flyouts, the avatar menu and the mobile nav sheet (the calendar opens
 * the same sheet), which all consume this model. The avatar menu's location
 * list and the Alerts list guard their own exits. (Browser Back / Forward is
 * not guarded — see NavigationGuardContext.)
 */
export function useNavModel(): NavModel {
  const navigate = useGuardedNavigate();
  // Unguarded, for redirects that happen AFTER an exit was already confirmed.
  const navigateUnguarded = useNavigate();
  const confirmLeave = useConfirmLeave();
  const { pathname } = useLocation();
  // SalonContext is untyped JS; cast at the boundary (established pattern).
  const {
    currentUser,
    businessInfo,
    availableLocations,
    selectedSalonId,
    openLocationPicker: openLocationPickerUnguarded,
  } = useSalon() as any;
  // The location picker replaces the whole app shell, so it is an exit too.
  const openLocationPicker = () =>
    confirmLeave(() => openLocationPickerUnguarded());
  const { resetAuth } = useAuth() as { resetAuth: () => void };
  const { showNavItem: showOnboardingNavItem } =
    useOnboardingDashboardVisibility();
  const { hasFeature, access } = useFeatureAccess();
  // Non-throwing: the badge is simply absent if the provider isn't mounted.
  const messaging = useOptionalMessaging();
  const perms = access?.section_permissions;

  const isOwner = access?.user_level === "Owner";
  const isOwnerOrManager =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";
  // Explicit === true checks fail closed when permissions are undefined.
  const hasPayrollPermission =
    perms?.settingsPayroll?.canAccess === true ||
    (perms?.settingsPayroll === undefined && isOwnerOrManager);
  // The role permission alone isn't enough: payroll must also be set up for
  // this location / user, or the page's RollFi calls fail (payrollAccess).
  const canAccessPayroll =
    hasPayrollPermission && canOpenPayroll({ currentUser, businessInfo });
  const stripeOnboarded = !!businessInfo?.stripe_connect_account_is_onboarded;
  const salesTabEnabled = isSalesTabEnabled();
  const giftCardsEnabled = isGiftCardsEnabled();

  const sections = useMemo(
    () =>
      buildNavSections({
        perms,
        hasFeature: hasFeature as NavAccess["hasFeature"],
        isOwner,
        isOwnerOrManager,
        canAccessPayroll,
        salesTabEnabled,
        giftCardsEnabled,
        stripeOnboarded,
      }),
    [
      perms,
      hasFeature,
      isOwner,
      isOwnerOrManager,
      canAccessPayroll,
      salesTabEnabled,
      giftCardsEnabled,
      stripeOnboarded,
    ],
  );

  const account: NavAccount | null = currentUser
    ? {
        name: `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim(),
        initials: initialsOf(currentUser.first_name, currentUser.last_name),
        role: access?.user_level || currentUser.role,
        salonName: businessInfo?.salon_name,
      }
    : null;

  const handleSignOut = () =>
    confirmLeave(async () => {
      try {
        await signOut();
        resetAuth();
        // Unsaved changes were already resolved by confirmLeave above.
        navigateUnguarded("/login");
      } catch (error) {
        console.error("Error signing out:", error);
      }
    });

  return {
    navigate,
    pathname,
    sections,
    activeSection: findActiveSection(sections, pathname),
    account,
    showLocationSwitcher: (availableLocations?.length || 0) > 1,
    availableLocations: availableLocations || NO_LOCATIONS,
    selectedSalonId: selectedSalonId ?? null,
    openLocationPicker,
    canAccessOnboarding: isOwner && showOnboardingNavItem,
    // Two-way SMS/MMS: REACT_APP_MESSAGING_ENABLED + a Twilio location + the
    // `messaging` permission (fail-closed: undefined = hidden).
    showChat:
      isMessagingEnabled() &&
      access?.messaging_platform === "twilio" &&
      perms?.messaging?.canView === true,
    chatUnreadCount: messaging?.totalUnreadCount ?? 0,
    handleSignOut,
  };
}
